import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import Logo from '../../components/Logo';
import { 
  Shield, 
  CheckCircle2, 
  Car, 
  Euro, 
  Upload,
  FileText,
  AlertTriangle,
  Wrench,
  Image
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

const GaragePortal = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteFile, setQuoteFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/portal/garage/${token}`);
        setData(response.data);
        if (response.data.quote_amount) {
          setSubmitted(true);
        }
      } catch (err) {
        setError('Ce lien est invalide ou a expiré.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQuoteFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!quoteFile || !quoteAmount) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', quoteFile);
      formData.append('amount', quoteAmount);

      await axios.post(`${API_URL}/portal/garage/${token}/quote`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSubmitted(true);
      toast.success('Devis soumis avec succès');
    } catch (err) {
      toast.error('Erreur lors de l\'envoi du devis');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-2xl mx-auto">
          <Skeleton className="h-12 w-48 mb-6" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="garage-portal">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-4 md:px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Logo size="default" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 md:p-8">
        {/* Claim Info */}
        <Card className="border-slate-100 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Dossier {data?.claim_reference}</CardTitle>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                {data?.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Car className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Véhicule entreprise</p>
                  <p className="font-medium">{data?.company_vehicle?.plate}</p>
                </div>
              </div>
              {data?.third_party_vehicle?.plate && (
                <div className="flex items-center gap-3">
                  <Car className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Véhicule tiers</p>
                    <p className="font-medium">{data?.third_party_vehicle?.plate}</p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500">Description de l'incident</p>
              <p className="font-medium">{data?.incident_description}</p>
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        {data?.documents?.length > 0 && (
          <Card className="border-slate-100 mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="w-5 h-5 text-slate-600" />
                Photos des dommages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {data.documents.map((doc) => (
                  <div key={doc.id} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <img
                      src={`${API_URL}/claims/${data.claim_id}/documents/${doc.id}`}
                      alt={doc.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quote Form */}
        {!submitted ? (
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-500" />
                Soumettre votre devis
              </CardTitle>
              <CardDescription>
                Veuillez établir un devis pour les réparations nécessaires
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="quote-amount">Montant du devis (€) *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="quote-amount"
                    type="number"
                    placeholder="0.00"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    className="pl-10 h-12 text-lg"
                    data-testid="quote-amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Document du devis (PDF) *</Label>
                <div 
                  className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-slate-300 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="quote-file-input"
                  />
                  {quoteFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-emerald-500" />
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{quoteFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(quoteFile.size / 1024).toFixed(1)} Ko
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="font-medium text-slate-700">Cliquez pour téléverser le devis</p>
                      <p className="text-sm text-slate-500 mt-1">PDF, DOC ou image</p>
                    </>
                  )}
                </div>
              </div>

              <Button 
                onClick={handleSubmit}
                disabled={submitting || !quoteFile || !quoteAmount}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                data-testid="submit-quote-btn"
              >
                {submitting ? 'Envoi en cours...' : 'Soumettre le devis'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="py-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-emerald-900 mb-2">Devis soumis</h2>
              <p className="text-emerald-700">
                Votre devis de <strong>{data?.quote_amount || quoteAmount}€</strong> a bien été enregistré.
              </p>
              <p className="text-sm text-emerald-600 mt-4">
                Le gestionnaire du dossier va examiner votre devis et vous recontactera.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Box */}
        <Alert className="mt-6">
          <Shield className="h-4 w-4" />
          <AlertTitle>Information</AlertTitle>
          <AlertDescription>
            Ce dossier est géré dans le cadre d'une résolution amiable par C2 AMIABLE, tiers de confiance.
            Le paiement sera effectué directement par l'entreprise responsable après validation.
          </AlertDescription>
        </Alert>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-slate-500 border-t border-slate-200 mt-8">
        <p>© 2025 C2 Assurances - Tiers de confiance</p>
      </footer>
    </div>
  );
};

export default GaragePortal;
